import React, { useState } from 'react';
import styled from 'styled-components';

import { EmailCampaignSignupDoubleBlockData } from 'api/cms';
import ButtonGroup, { Button } from 'components/ButtonGroup';
import { FormSubmitFunction } from 'components/Form';
import { colors, mediaRetina, transition } from 'theme';
import { postJson } from 'utils/http';

import { EmailCampaignSignup } from './EmailCampaignSignup';

const ToggleButton = styled(Button)<{ selected?: boolean }>`
  width: 50%;
  padding: 12px;
  border: none;
  box-shadow: 0 0 0 1px white inset;
  ${mediaRetina} {
    box-shadow: 0 0 0 1.5px white inset;
  }
  overflow: hidden;
  transition: all ${transition};
  &:first-of-type {
    border-radius: 4px 0 0 4px;
  }
  &:last-of-type {
    border-radius: 0 4px 4px 0;
  }
  ${(props) =>
    props.selected
      ? `
    background: white;
    color: ${colors.waterBlue};
    &:hover, &:focus {
      background: white;
      color: ${colors.waterBlue};
      ${mediaRetina} {
        box-shadow: 0 0 0 1.5px white inset;
      }
    }
  `
      : `
    background: transparent;
    color: white;
    border: 0;
    &:hover, &:focus {
      background: ${colors.turtleGreen};
      box-shadow: 0 0 0 1px ${colors.turtleGreen} inset;
      ${mediaRetina} {
        box-shadow: 0 0 0 1.5px ${colors.turtleGreen} inset;
      }
    }
  `}
`;

type Props = {
  blockId: string;
  data: EmailCampaignSignupDoubleBlockData;
  isSidebar?: boolean;
};

export function EmailCampaignSignupDoubleBlock({
  blockId,
  data: {
    headline,
    toggleTextOne,
    toggleTextTwo,
    buttonTextOne,
    buttonTextTwo,
    bgImage,
  },
  isSidebar,
}: Props) {
  const [mode, setMode] = useState('initial');
  const [isOne, setIsOne] = useState(true);

  const onSubmit: FormSubmitFunction<
    EmailCampaignSignupDoubleBlockData & { index: number }
  > = ({ values, handleError }) => {
    const postBaseUrl = isSidebar
      ? '/data/website/sidebar-email-signup-double/'
      : '/data/website/page-email-signup-double/';
    setMode('submitting');
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-param-reassign
    values.index = isOne ? 0 : 1;
    postJson(postBaseUrl + blockId, values)
      .then(() => {
        setMode('submitted');
      })
      .catch((e) => {
        handleError(e);
        setMode('initial');
      });
  };

  return (
    <EmailCampaignSignup
      disabled={mode === 'submitting'}
      onSubmit={onSubmit}
      headline={headline}
      bgImage={bgImage}
      buttonText={isOne ? buttonTextOne : buttonTextTwo}
      preForm={
        <ButtonGroup mb="10px">
          <ToggleButton
            key="one"
            onClick={() => setIsOne(true)}
            selected={isOne}
            tabIndex={isOne ? -1 : 0}
            data-qa-id="cms-email-campaign-signup-toggle-one"
          >
            {toggleTextOne}
          </ToggleButton>
          <ToggleButton
            key="two"
            onClick={() => setIsOne(false)}
            selected={!isOne}
            tabIndex={!isOne ? -1 : 0}
            data-qa-id="cms-email-campaign-signup-toggle-two"
          >
            {toggleTextTwo}
          </ToggleButton>
        </ButtonGroup>
      }
      isSidebar={isSidebar}
      isSubmitted={mode === 'submitted'}
      qaId="cms-email-campaign-signup-double"
    />
  );
}
